import { AppIcon } from '@/components/ui/app-icon';
import { reportError } from '@/lib/telemetry';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { iconArrowsRotate, iconCircleExclamation } from '@/lib/icons';

interface Props {
  children: ReactNode;
  /** Which boundary caught it, as error reports tag it: 'dock', 'chat', ... */
  name?: string;
  /**
   * What shows in place of the chat after a crash; `retry` renders the chat
   * again. Defaults to the message filling the space the chat had, so a
   * boundary inside the panel keeps it there. A boundary in the shell's row
   * passes one that stands in the panel's own frame, or renders nothing when
   * the panel is closed, so the page beside it never moves.
   */
  fallback?: (retry: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
}

/** The message and Try again, filling the space the chat had. */
export function AgentChatErrorFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center'>
      <AppIcon
        icon={iconCircleExclamation}
        className='text-muted-foreground'
        style={{ fontSize: 32 }}
      />
      <p className='text-sm text-muted-foreground'>
        Something went wrong with Clu.
      </p>
      <Button variant='outline' size='sm' onClick={onRetry}>
        <AppIcon
          icon={iconArrowsRotate}
          className='mr-1.5'
          style={{ fontSize: 14 }}
        />
        Try again
      </Button>
    </div>
  );
}

export class AgentChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('AgentChat error:', error, info.componentStack);
    // Without this a crash here only shows its fallback, and nobody hears.
    reportError(error, {
      tags: {
        surface: 'agent_chat',
        ...(this.props.name && { boundary: this.props.name }),
      },
      extra: { componentStack: info.componentStack },
    });
  }

  private retry = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      return this.props.fallback ? (
        this.props.fallback(this.retry)
      ) : (
        <AgentChatErrorFallback onRetry={this.retry} />
      );
    }

    return this.props.children;
  }
}
